/**
 * Maps a failed project save to the message the user sees.
 *
 * Two error shapes reach here:
 *  - HTTP failures: error.code is `HTTP_<status>` and error.details is the raw JSON body. The body
 *    may be the standard envelope (`Errors: [...]`), ASP.NET ValidationProblemDetails
 *    (`errors: { field: [...] }`), or ProblemDetails (`title` / `detail`).
 *  - Envelope failures (HTTP 200 with `Success: false`): error.code is the first error's business
 *    code, and error.details is the PascalCase body.
 *
 * Raw API and SQL detail must not reach the user, so only messages that pass the checks below are
 * shown. Anything else, including malformed input, gets the generic message. This function never throws.
 */

export const USER_SAVE_ERROR = 'Could not save the project. Please try again or contact support.';

// HTTP statuses whose messages are written for the user. 5xx bodies can carry internal detail.
const USER_VISIBLE_HTTP_CODES = new Set(['HTTP_400', 'HTTP_409', 'HTTP_422']);

// Envelope codes that name a server-side failure. Their messages can carry internal detail, so they
// get the generic message. Any other business code (for example DUPLICATE_NAME, or a code the backend
// adds later) shows its message, provided the text passes INTERNAL_DETAIL below. This is a deny-list
// on purpose: an allowlist hid valid messages whenever the backend added a new business code.
const SERVER_FAILURE_CODE = /UNEXPECTED|INTERNAL|EXCEPTION|SERVER|UNHANDLED|FAILURE|^ERROR$|^UNKNOWN$/i;

// SQL, driver, and stack-trace signatures. Kept specific so ordinary words such as "table" or
// "column" in a validation message ("Table name is required") still pass.
const INTERNAL_DETAIL =
  /\bdbo\.|\bSQL\b|Invalid column name|Cannot insert the value|Violation of|\bException\b|Object reference|Stack trace/i;

function asArray(value: unknown): unknown[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function messagesFrom(envelope: any): string[] {
  const found: string[] = [];
  const add = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) found.push(value.trim());
  };

  // Envelope error records (`Errors` / `errors` arrays of { Message } or plain strings).
  for (const record of [...asArray(envelope?.Errors), ...asArray(envelope?.errors)]) {
    if (typeof record === 'string') add(record);
    else if (record && typeof record === 'object') add((record as any).Message ?? (record as any).message);
  }

  // ValidationProblemDetails: `errors` is { fieldName: [messages] }.
  const fieldErrors = envelope?.errors;
  if (fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)) {
    for (const messages of Object.values(fieldErrors)) asArray(messages).forEach(add);
  }

  // ProblemDetails `detail` and single-message bodies. `title` is deliberately skipped: for
  // validation failures it is a generic phrase such as "One or more validation errors occurred."
  add(envelope?.detail);
  add(envelope?.message ?? envelope?.Message);

  return found;
}

export function userFacingProjectError(error: any): string {
  try {
    const code = typeof error?.code === 'string' ? error.code : '';
    const isHttpError = code.startsWith('HTTP_');
    // A failure with no code at all is malformed, so it gets the generic message.
    const allowed = isHttpError
      ? USER_VISIBLE_HTTP_CODES.has(code)
      : code !== '' && !SERVER_FAILURE_CODE.test(code);
    if (!allowed) return USER_SAVE_ERROR;

    const envelope = error?.details && typeof error.details === 'object' ? error.details : {};
    const candidates = messagesFrom(envelope);

    // Envelope failures carry their message on the error itself as well.
    if (!isHttpError && candidates.length === 0 && typeof error?.message === 'string') {
      candidates.push(error.message);
    }

    const safe = [...new Set(candidates.filter((message) => !INTERNAL_DETAIL.test(message)))];
    return safe.length > 0 ? safe.join('\n') : USER_SAVE_ERROR;
  } catch {
    return USER_SAVE_ERROR;
  }
}
